import { Trash01 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import {
  SEVERITY_META,
  SEVERITY_ORDER,
  type Severity,
  severityToTier,
  tierToSeverity,
} from "@/components/safety-config/risk-groups";
import { cx } from "@/utils/cx";
import { MODEL_TYPE_LABELS, slugifyRiskId } from "../risk-config-v2/config-fields";
import { ComponentBadges } from "./ComponentBadges";
import { ConfigureRiskAccordion } from "./ConfigureRiskAccordion";
import type { CatalogRisk, RiskState } from "./useSafetyConfigState";

const SEVERITY_OPTIONS: { id: Severity; label: string }[] = SEVERITY_ORDER.map((id) => ({
  id,
  label: `${SEVERITY_META[id].label} Severity`,
}));

type AdminRiskRowProps = {
  risk: CatalogRisk;
  state: RiskState;
  hasChanges: boolean;
  isConfigOpen: boolean;
  onToggleConfig: () => void;
  effectiveRisk: (risk: CatalogRisk) => CatalogRisk;
  onToggle: (risk: CatalogRisk, enabled: boolean) => void;
  onUpdate: (riskId: string, patch: Partial<RiskState>) => void;
  onSetName: (risk: CatalogRisk, name: string) => void;
  onSetDescription: (risk: CatalogRisk, description: string) => void;
  onSetVision: (localKey: string, vision: boolean) => void;
  onRemoveNew: (localKey: string) => void;
  onDelete: (risk: CatalogRisk) => void;
};

export const AdminRiskRow = ({
  risk,
  state,
  hasChanges,
  isConfigOpen,
  onToggleConfig,
  effectiveRisk,
  onToggle,
  onUpdate,
  onSetName,
  onSetDescription,
  onSetVision,
  onRemoveNew,
  onDelete,
}: AdminRiskRowProps) => {
  const isCustom = risk.isNew || risk.category === "custom";
  const severity = tierToSeverity(state.tier);

  const badges = (
    <span className="flex items-center gap-2">
      <Badge size="sm" color="gray">
        {MODEL_TYPE_LABELS[risk.modelType]}
      </Badge>
      <ComponentBadges modelType={risk.modelType} config={state.config} />
      {risk.vision && (
        <Tooltip title="This risk analyzes video frames as well as the transcript. Without vision, detection runs on the transcript only.">
          <TooltipTrigger className="cursor-default">
            <Badge size="sm" color="blue">
              Vision
            </Badge>
          </TooltipTrigger>
        </Tooltip>
      )}
    </span>
  );

  return (
    <div className="px-6 py-5 border-t border-secondary first:border-t-0 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        {risk.isNew ? (
          // Staged new custom risk: name stays editable until save. The id is the
          // slugified name, previewed here and resolved for real on save. A staged
          // risk toggled off is skipped at save time (not created).
          <div className="flex flex-1 items-start gap-2">
            <Checkbox
              isSelected={state.enabled}
              onChange={(checked) => onToggle(risk, checked)}
              size="sm"
              className="mt-2.5"
              aria-label={`Enable ${risk.readableName || "new custom risk"}`}
            />
            <div className="flex flex-1 flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <Input
                  aria-label="Custom risk name"
                  placeholder="Custom risk name"
                  value={risk.readableName}
                  onChange={(value: string) => onSetName(risk, value)}
                />
                {badges}
              </div>
              <span className="text-xs text-tertiary">
                id: <span className="font-mono">{slugifyRiskId(risk.readableName) || "—"}</span>
              </span>
            </div>
          </div>
        ) : risk.category === "custom" ? (
          // Persisted custom risk: the name stays editable (id is immutable), so
          // the enable checkbox is pulled out of the label to avoid toggling it
          // when clicking into the input.
          <div className="flex flex-1 items-start gap-2">
            <Checkbox
              isSelected={state.enabled}
              onChange={(checked) => onToggle(risk, checked)}
              size="sm"
              className="mt-2.5"
              aria-label={`Enable ${effectiveRisk(risk).readableName}`}
            />
            <div className="flex flex-1 flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <Input
                  aria-label="Custom risk name"
                  placeholder="Custom risk name"
                  value={effectiveRisk(risk).readableName}
                  onChange={(value: string) => onSetName(risk, value)}
                />
                {badges}
              </div>
              <span className="text-xs text-tertiary">
                id: <span className="font-mono">{risk.id}</span>
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 items-start gap-2">
            <Checkbox
              isSelected={state.enabled}
              onChange={(checked) => onToggle(risk, checked)}
              size="sm"
              className="mt-0.5"
              aria-label={`Enable ${risk.readableName}`}
            />
            <div className="flex flex-1 flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className={cx("text-sm font-medium", state.enabled ? "text-primary" : "text-tertiary")}>
                  {risk.readableName}
                </span>
                {badges}
              </span>
              {risk.readableDescription && <span className="text-sm text-tertiary">{risk.readableDescription}</span>}
              <span className="text-xs text-quaternary font-mono">{risk.id}</span>
            </div>
          </div>
        )}
        {state.enabled ? (
          <div className="flex items-center gap-3 flex-shrink-0">
            {risk.isNew && (
              <Checkbox
                isSelected={risk.vision ?? false}
                onChange={(checked) => onSetVision(risk.id, checked)}
                size="sm"
                label="Vision"
              />
            )}
            <div className="w-36">
              <Select
                aria-label={`Severity for ${risk.readableName}`}
                size="sm"
                className="[&_button_span]:!py-1.5 [&_p]:!text-sm"
                popoverClassName="!w-auto min-w-36"
                items={SEVERITY_OPTIONS}
                selectedKey={severity}
                onSelectionChange={(key) => onUpdate(risk.id, { tier: severityToTier(key as Severity) })}
              >
                {(item) => (
                  <Select.Item id={item.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: SEVERITY_META[item.id as Severity].color }}
                      />
                      {item.label}
                    </span>
                  </Select.Item>
                )}
              </Select>
            </div>
            {risk.isNew ? (
              <Button
                size="sm"
                color="tertiary"
                iconLeading={Trash01}
                onClick={() => onRemoveNew(risk.id)}
                aria-label="Remove custom risk"
              >
                {""}
              </Button>
            ) : (
              risk.category === "custom" && (
                <Button
                  size="sm"
                  color="tertiary"
                  iconLeading={Trash01}
                  onClick={() => onDelete(risk)}
                  isDisabled={hasChanges}
                  title={hasChanges ? "Save or discard your changes first" : "Delete custom risk"}
                  aria-label="Delete custom risk"
                >
                  {""}
                </Button>
              )
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3 flex-shrink-0">
            <Badge size="sm" color="gray">
              Disabled
            </Badge>
            {risk.isNew && (
              <Button
                size="sm"
                color="tertiary"
                iconLeading={Trash01}
                onClick={() => onRemoveNew(risk.id)}
                aria-label="Remove custom risk"
              >
                {""}
              </Button>
            )}
          </div>
        )}
      </div>
      {state.enabled && (
        <div className="pl-7 flex flex-col gap-3">
          <ConfigureRiskAccordion
            risk={risk}
            state={state}
            isOpen={isConfigOpen}
            onToggle={onToggleConfig}
            onConfigChange={(config) => onUpdate(risk.id, { config })}
            descriptionValue={isCustom ? effectiveRisk(risk).readableDescription : undefined}
            onDescriptionChange={isCustom ? (value) => onSetDescription(risk, value) : undefined}
          />
        </div>
      )}
    </div>
  );
};
